import type { UndoRedoState, MemoryBookSnapshot, RestoredHistory } from './types'

const STORAGE_KEY = 'memoryBooks_undoRedoHistory'

export function saveHistoryToStorage(
  historyState: UndoRedoState,
  bookId: string,
  currentSnapshot: MemoryBookSnapshot | null = null
): boolean {
  if (!bookId || !historyState) {
    return false
  }

  try {
    const dataToSave = {
      bookId,
      undoStack: historyState.undoStack,
      redoStack: historyState.redoStack,
      currentSnapshot,
      timestamp: Date.now(),
    }

    const serialized = JSON.stringify(dataToSave)
    sessionStorage.setItem(STORAGE_KEY, serialized)
    return true
  } catch (error) {
    console.error('Failed to save history to sessionStorage:', error)

    // Attempt to clear old data and retry once
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      try {
        sessionStorage.removeItem(STORAGE_KEY)
        const serialized = JSON.stringify({
          bookId,
          undoStack: historyState.undoStack.slice(-10),
          redoStack: historyState.redoStack.slice(-10),
          currentSnapshot,
          timestamp: Date.now(),
        })
        sessionStorage.setItem(STORAGE_KEY, serialized)
        return true
      } catch (retryError) {
        console.error('Retry failed:', retryError)
        return false
      }
    }

    return false
  }
}

export function loadHistoryFromStorage(bookId: string): RestoredHistory | null {
  if (!bookId) {
    return null
  }

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)

    if (!stored) {
      return null
    }

    const parsed = JSON.parse(stored)

    // Validate structure
    if (!parsed?.bookId || !Array.isArray(parsed?.undoStack) || !Array.isArray(parsed?.redoStack)) {
      sessionStorage.removeItem(STORAGE_KEY)
      return null
    }

    // Only restore if it's for the same book
    if (parsed.bookId !== bookId) {
      sessionStorage.removeItem(STORAGE_KEY)
      return null
    }

    // Check if data is stale (older than 1 hour)
    const ONE_HOUR = 60 * 60 * 1000
    if (parsed.timestamp && Date.now() - parsed.timestamp > ONE_HOUR) {
      sessionStorage.removeItem(STORAGE_KEY)
      return null
    }

    return {
      undoStack: parsed.undoStack,
      redoStack: parsed.redoStack,
      currentSnapshot: parsed.currentSnapshot || null,
    }
  } catch (error) {
    console.error('Failed to load history from sessionStorage:', error)
    sessionStorage.removeItem(STORAGE_KEY)
    return null
  }
}

export function clearHistoryFromStorage(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch (error) {
    console.error('Failed to clear history from sessionStorage:', error)
  }
}
